// src/utils/share-v2.js
// DECOUPLED SHARING ARCHITECTURE
// - Separates album structure (pages, text, layout) from photo data
// - Uploads structure as one small encrypted file
// - Uploads each photo separately to Firebase Storage
// - Loads progressively for smooth performance with large albums
// - 🆕 NEW: Tracks shares locally for management/deletion

import LZString from 'lz-string';
import { db, storage, ensureAuth, waitForAuthReady } from '../firebaseClient';
import { ref, uploadBytes, getBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { doc, setDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { analyzeAndPrint, estimateOptimalSize } from './payloadAnalyzer';

// 🆕 NEW: Import share tracking store
import { saveShareRecord, deleteShareRecord, getShareRecord } from './shareStore';

console.log('[Storage Bucket]', storage.app?.options?.storageBucket);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_INLINE_HASH_CHARS = 2000;
const MAX_INLINE_JSON_BYTES = 400 * 1024;
const STORAGE_PREFIX = 'S-';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function randomId(len = 16) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  const array = new Uint8Array(len);
  window.crypto.getRandomValues(array);
  for (let i = 0; i < len; i++) {
    out += chars[array[i] % chars.length];
  }
  return out;
}

function isDataUrl(u) {
  return typeof u === 'string' && u.startsWith('data:');
}

function stableIdFromDataUrl(dataUrl) {
  try {
    const b64 = (dataUrl || '').split(',')[1] || dataUrl || '';
    // Fast deterministic hash (djb2 xor variant)
    let h = 5381;
    for (let i = 0; i < b64.length; i++) {
      h = ((h << 5) + h) ^ b64.charCodeAt(i);
    }
    return 'ph_' + (h >>> 0).toString(36);
  } catch {
    return 'ph_' + Math.random().toString(36).slice(2, 10);
  }
}

/**
 * Derive a stable ID for any photo-like object:
 * - Prefer explicit ids (id/photoId)
 * - If only a data URL is present, hash it to a short, safe id
 */
function derivePhotoId(obj) {
  const url = obj?.src || obj?.url;
  const base = obj?.id || obj?.photoId;
  if (base) return String(base);
  if (isDataUrl(url)) return stableIdFromDataUrl(url);
  return undefined;
}


// ---------------------------------------------------------------------------
// Inline encode/decode (for small payloads - backwards compatibility)
// ---------------------------------------------------------------------------

export function encodeAlbumPayload(payload) {
  try {
    if (!payload || typeof payload !== 'object') {
      throw new Error('Invalid payload: must be an object');
    }
    const json = JSON.stringify(payload);
    const compressed = LZString.compressToEncodedURIComponent(json);
    return compressed;
  } catch (err) {
    console.error('[encodeAlbumPayload] Failed:', err);
    return null;
  }
}

export function decodeAlbumPayload(hash) {
  if (!hash) return null;
  if (hash.startsWith(STORAGE_PREFIX)) {
    // new storage style; handled by loadStoredAlbumFromHash
    return null;
  }
  try {
    const json = LZString.decompressFromEncodedURIComponent(hash);
    if (!json) return null;
    return JSON.parse(json);
  } catch (err) {
    console.error('[decodeAlbumPayload] Failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Duration helpers (used by AlbumFromPast)
// ---------------------------------------------------------------------------

export function calculateTotalDuration(pages = []) {
  if (!Array.isArray(pages)) return 0;
  return pages.reduce((sum, p) => {
    const d = typeof p.durationMs === 'number' ? p.durationMs : 0;
    return sum + d;
  }, 0);
}

export function formatStoryDuration(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function arrayBufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64urlToArrayBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function isCryptoAvailable() {
  return typeof window !== 'undefined' &&
    !!window.crypto &&
    !!window.crypto.subtle;
}

// ---------------------------------------------------------------------------
// Photo Processing
// ---------------------------------------------------------------------------

/**
 * Convert base64 data URL to Blob
 */
function base64ToBlob(dataUrl) {
  const parts = dataUrl.split(',');
  if (parts.length !== 2) {
    throw new Error('Invalid data URL format');
  }
  
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  
  const bstr = atob(parts[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  
  return new Blob([u8arr], { type: mime });
}

/**
 * Extract all unique photos from payload
 * Returns Map of photoId -> { url, blob, metadata }
 */
function extractUniquePhotos(payload) {
  const photoMap = new Map();

  // 🔬 DIAGNOSTIC - What photos are we receiving?
  console.log('🔬 extractUniquePhotos called with', payload.photos?.length || 0, 'photos');
  if (payload.photos?.[0]) {
    const first = payload.photos[0];
    console.log('🔬 First photo url length:', first.url?.length, '| thumbnail length:', first.thumbnail?.length);
    console.log('🔬 url starts with:', first.url?.substring(0, 50));
    console.log('🔬 thumbnail starts with:', first.thumbnail?.substring(0, 50));
  }
  
  // Check payload.photos array (if it exists)
  if (Array.isArray(payload.photos)) {
    payload.photos.forEach(photo => {
      const id = derivePhotoId(photo);
 if (id && isDataUrl(photo.url)) {
        photoMap.set(id, {
          url: photo.url,
          id,
          metadata: {
            fileName: photo.fileName,
            timestamp: photo.timestamp,
            gps: photo.gps
          }
        });
      }
    });
  }
  
  // Also check pages for embedded photos
  if (Array.isArray(payload.pages)) {
    payload.pages.forEach(page => {
      // Check page.items
      if (Array.isArray(page.items)) {
        page.items.forEach(item => {
          const id = derivePhotoId(item);
 if (id && isDataUrl(item.url) && !photoMap.has(id)) {
            photoMap.set(id, {
              url: item.url,
              id,
              metadata: {
                fileName: item.fileName
              }
            });
          }
        });
      }
      
      // Check page.photos
      if (Array.isArray(page.photos)) {
        page.photos.forEach(photo => {
          const url = photo.src || photo.url;
 const id = derivePhotoId({ ...photo, url, src: url });
 if (id && isDataUrl(url) && !photoMap.has(id)) {
            photoMap.set(id, {
              url,
              id,
              metadata: {
                fileName: photo.fileName
              }
            });
          }
        });
      }
    });
  }
  
  console.log(`[extractUniquePhotos] Found ${photoMap.size} unique photos`);
  return photoMap;
}

/**
 * Upload a single photo to Firebase Storage
 */
async function uploadPhoto(shareId, photoId, dataUrl, onProgress) {
  const blob = base64ToBlob(dataUrl);
  const ext = blob.type.includes('png') ? 'png' : 'jpg';
  // Never let a data URL (or weird chars) become a filename
  const fallbackId = stableIdFromDataUrl(dataUrl);
  const safeId = /^(data:)/.test(String(photoId))
    ? fallbackId
    : String(photoId).replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 64);
  const path = `shares/${shareId}/photos/${safeId}.${ext}`;
  
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, blob, {
    contentType: blob.type
  });
  
  if (onProgress) {
    onProgress(photoId);
  }
  
  return { photoId, storagePath: path };
}

/**
 * Upload photos in parallel batches
 */
async function uploadPhotosInBatches(shareId, photoMap, onProgress) {
  const BATCH_SIZE = 5; // Upload 5 photos at a time
  const entries = Array.from(photoMap.entries());
  const results = new Map();
  
  let completed = 0;
  const total = entries.length;
  
  for (let i = 0; i < entries.length; i += BATCH_SIZE) {
    const batch = entries.slice(i, i + BATCH_SIZE);
    
    const batchPromises = batch.map(([photoId, photoData]) =>
      uploadPhoto(shareId, photoId, photoData.url, () => {
        completed++;
        if (onProgress) {
          onProgress({ completed, total, photoId });
        }
      })
    );
    
    const batchResults = await Promise.all(batchPromises);
    batchResults.forEach(({ photoId, storagePath }) => {
      results.set(photoId, storagePath);
    });
    
    console.log(`[uploadPhotosInBatches] Completed ${completed}/${total} photos`);
  }
  
  return results;
}

// ---------------------------------------------------------------------------
// Structure Processing
// ---------------------------------------------------------------------------

/**
 * Remove base64 data from payload and replace with storage paths
 */
function createStructurePayload(payload, photoStoragePaths) {
  const structure = JSON.parse(JSON.stringify(payload)); // Deep clone
  
  // Replace photos array
  if (Array.isArray(structure.photos)) {
    structure.photos = structure.photos.map(photo => {
      const id = derivePhotoId(photo);
      const storagePath = photoStoragePaths.get(id);
      
      return {
        ...photo,
        url: undefined, // Remove base64 data
        storagePath: storagePath || null,
        id
      };
    });
  }
  
  // Replace photos in pages
  if (Array.isArray(structure.pages)) {
    structure.pages = structure.pages.map(page => {
      const newPage = { ...page };
      
      // Handle page.items
      if (Array.isArray(newPage.items)) {
        newPage.items = newPage.items.map(item => {
          const id = derivePhotoId(item);
          const storagePath = photoStoragePaths.get(id);
          
          return {
            ...item,
            url: undefined, // Remove base64 data
            storagePath: storagePath || null,
            photoId: id
          };
        });
      }
      
      // Handle page.photos
      if (Array.isArray(newPage.photos)) {
        newPage.photos = newPage.photos.map(photo => {
          const url = photo.src || photo.url;
 const id = derivePhotoId({ ...photo, url, src: url });
 const storagePath = photoStoragePaths.get(id);
          
          return {
            ...photo,
            url: undefined,
            src: undefined, // Remove base64 data
            storagePath: storagePath || null,
            id
          };
        });
      }
      
      return newPage;
    });
  }
  
  return structure;
}

// ---------------------------------------------------------------------------
// Encryption (same as before)
// ---------------------------------------------------------------------------

async function generateAesKey() {
  const key = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const raw = await window.crypto.subtle.exportKey('raw', key);
  return { key, raw };
}

async function encryptBytes(key, dataBytes) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    dataBytes
  );
  return { iv, ciphertext };
}

// ---------------------------------------------------------------------------
// 🆕 NEW: Delete Share Function
// ---------------------------------------------------------------------------

/**
 * Delete a shared album from Firebase Storage and Firestore
 * Also removes the local tracking record
 * 
 * @param {string} shareId - The share ID to delete
 * @returns {Promise<boolean>} Success status
 */
// ---------------------------------------------------------------------------
// 🆕 NEW: Delete Share Function (hardened)
// ---------------------------------------------------------------------------

/**
 * Delete a shared album from Firebase Storage and Firestore
 * Also removes the local tracking record
 *
 * Returns TRUE only if the remote share is actually gone (at least structure.enc).
 */
export async function deleteShare(shareId) {
  console.log(`[deleteShare] 🗑️ Deleting share: ${shareId}`);

  if (!shareId) {
    console.error('[deleteShare] No shareId provided');
    return false;
  }

  try {
    // On Android WebView this matters: wait for auth before touching Storage
    if (typeof waitForAuthReady === 'function') {
      await waitForAuthReady();
    }

    await ensureAuth();

    if (!storage || !db) {
      throw new Error('Firebase not initialized');
    }

    // Get local record for stored paths
    const localRecord = await getShareRecord(shareId);

    // Helper to detect hard permission failures that should abort
    const isHardPermissionError = (err) => {
      const code = err?.code || '';
      return (
        code === 'storage/unauthorized' ||
        code === 'storage/unauthenticated' ||
        code === 'permission-denied'
      );
    };

    let hadHardFailure = false;

    // Approach 1: Use stored paths if available (more reliable)
    if (localRecord?.storagePaths && localRecord.storagePaths.length > 0) {
      console.log(`[deleteShare] Deleting ${localRecord.storagePaths.length} stored files.`);

      const deleteResults = await Promise.all(
        localRecord.storagePaths.map(async (path) => {
          try {
            const fileRef = ref(storage, path);
            await deleteObject(fileRef);
            console.log(`[deleteShare] ✅ Deleted: ${path}`);
            return true;
          } catch (err) {
            const msg = err?.message || String(err);
            const code = err?.code || 'unknown';
            if (code === 'storage/object-not-found') {
              console.log(`[deleteShare] File already gone: ${path}`);
              return true;
            }
            console.warn(`[deleteShare] ❌ Failed to delete: ${path} (${code})`, msg);
            if (isHardPermissionError(err)) hadHardFailure = true;
            return false;
          }
        })
      );

      // If we have permission errors, stop and report failure
      if (hadHardFailure) {
        console.warn('[deleteShare] Aborting: permission/auth failure while deleting stored paths.');
        return false;
      }
    } else {
      // Approach 2: Try to delete structure + list photos
      console.log('[deleteShare] No stored paths, attempting folder cleanup.');

      // Delete structure.enc (do NOT swallow meaningful errors)
      try {
        const structureRef = ref(storage, `shares/${shareId}/structure.enc`);
        await deleteObject(structureRef);
        console.log('[deleteShare] ✅ Deleted: structure.enc');
      } catch (err) {
        const msg = err?.message || String(err);
        const code = err?.code || 'unknown';
        if (code !== 'storage/object-not-found') {
          console.warn(`[deleteShare] ❌ Failed to delete structure.enc (${code})`, msg);
          if (isHardPermissionError(err)) return false;
          // If structure cannot be deleted for other reasons, treat as failure
          return false;
        }
      }

      // Best-effort list/delete photos; if listAll is blocked, you may not be able to delete photos here
      try {
        const photosRef = ref(storage, `shares/${shareId}/photos`);
        const photosList = await listAll(photosRef);

        for (const item of photosList.items) {
          try {
            await deleteObject(item);
          } catch (err) {
            const msg = err?.message || String(err);
            const code = err?.code || 'unknown';
            if (code !== 'storage/object-not-found') {
              console.warn(`[deleteShare] Failed to delete photo: ${item.fullPath} (${code})`, msg);
              if (isHardPermissionError(err)) return false;
            }
          }
        }

        console.log(`[deleteShare] Deleted ${photosList.items.length} photos`);
      } catch (listErr) {
        // listAll might not be allowed by storage rules; log clearly
        const msg = listErr?.message || String(listErr);
        const code = listErr?.code || 'unknown';
        console.warn(`[deleteShare] Could not list photos folder (${code}):`, msg);
        // Do not fail solely because listAll is blocked; structure.enc deletion is the key invalidation
      }
    }

    // VERIFY: If structure.enc still exists, the link is still live (fail)
    try {
      const checkRef = ref(storage, `shares/${shareId}/structure.enc`);
      await getDownloadURL(checkRef);
      console.warn('[deleteShare] Verification failed: structure.enc still exists, share is still active.');
      return false;
    } catch (err) {
      const code = err?.code || '';
      if (code === 'storage/object-not-found') {
        console.log('[deleteShare] ✅ Verified: structure.enc is gone.');
      } else if (isHardPermissionError(err)) {
        console.warn('[deleteShare] Verification inconclusive due to permissions:', err?.message || err);
        return false;
      } else {
        // If it cannot fetch URL for other reasons, be conservative
        console.warn('[deleteShare] Verification inconclusive:', err?.message || err);
        return false;
      }
    }

    // Delete Firestore metadata document (best-effort)
    try {
      const metaRef = doc(db, 'shares', shareId);
      await deleteDoc(metaRef);
      console.log('[deleteShare] ✅ Deleted Firestore metadata');
    } catch (err) {
      console.warn('[deleteShare] Firestore delete failed:', err?.message || err);
      // Do not fail if metadata is absent; the main invalidation is Storage removal
    }

    // Delete local tracking record only after remote invalidation is verified
    await deleteShareRecord(shareId);

    console.log(`[deleteShare] ✅ Share ${shareId} deleted successfully`);
    return true;
  } catch (err) {
    console.error('[deleteShare] ❌ Failed to delete share:', err);
    return false;
  }
}


// ---------------------------------------------------------------------------
// Decoupled Upload
// ---------------------------------------------------------------------------

/**
 * Main upload function - uploads structure and photos separately
 */
export async function uploadDecoupledAlbum(payload, mode = 'story', onProgress) {
  console.log('\n🚀 === DECOUPLED ALBUM UPLOAD === 🚀');

  // Ensure Android WebView has an auth user before touching Storage
  await waitForAuthReady();   // <-- critical on Android

  await ensureAuth(); // ← prevents 401 on first visit to vercel.app
  
  if (!db || !storage) {
    throw new Error('Firebase not initialized');
  }
  
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto not available');
  }
  
  const shareId = randomId(16);
  console.log(`[uploadDecoupledAlbum] Share ID: ${shareId}`);
  
  // Step 1: Extract and upload photos
  console.log('[uploadDecoupledAlbum] 📸 Extracting unique photos...');
  const photoMap = extractUniquePhotos(payload);
  
  if (photoMap.size === 0) {
    console.warn('[uploadDecoupledAlbum] No photos found in payload');
  }
  
  console.log(`[uploadDecoupledAlbum] 📤 Uploading ${photoMap.size} photos...`);
  const photoStoragePaths = await uploadPhotosInBatches(
    shareId, 
    photoMap, 
    (progress) => {
      if (onProgress) {
        onProgress({
          phase: 'photos',
          ...progress
        });
      }
    }
  );
  
  // Step 2: Create structure without base64 data
  console.log('[uploadDecoupledAlbum] 📋 Creating structure payload...');
  const structure = createStructurePayload(payload, photoStoragePaths);
  
  const structureJson = JSON.stringify(structure);
  const structureBytes = new TextEncoder().encode(structureJson);
  console.log(`[uploadDecoupledAlbum] Structure size: ${(structureBytes.byteLength / 1024).toFixed(2)} KB`);
  
  // Step 3: Encrypt and upload structure
  console.log('[uploadDecoupledAlbum] 🔐 Encrypting structure...');
  const { key, raw } = await generateAesKey();
  const { iv, ciphertext } = await encryptBytes(key, structureBytes);
  
  const structurePath = `shares/${shareId}/structure.enc`;
  console.log(`[uploadDecoupledAlbum] 📤 Uploading structure to: ${structurePath}`);
  
  const fileRef = ref(storage, structurePath);
  
  // ✅ FIX: Use Blob instead of Uint8Array to guarantee contentType is applied
  const encryptedBlob = new Blob([new Uint8Array(ciphertext)], { 
    type: 'application/octet-stream' 
  });
  await uploadBytes(fileRef, encryptedBlob, {
    contentType: 'application/octet-stream'
  });
  
  if (onProgress) {
    onProgress({ phase: 'structure', completed: true });
  }
  
  // Step 4: Create Firestore metadata
  console.log('[uploadDecoupledAlbum] 💾 Creating metadata...');
  try {
    const metaRef = doc(db, 'shares', shareId);
    await setDoc(metaRef, {
      createdAt: serverTimestamp(),
      mode,
      structureBytes: structureBytes.byteLength,
      photoCount: photoMap.size,
      version: 2, // Version 2 = decoupled architecture
    });
  } catch (err) {
    console.warn('[uploadDecoupledAlbum] Metadata save failed:', err);
  }
  
  // Step 5: Generate hash
  const keyB64 = arrayBufferToBase64url(raw);
  const ivB64 = arrayBufferToBase64url(iv.buffer);
  const hash = `${STORAGE_PREFIX}${shareId}.${keyB64}.${ivB64}`;
  
  // 🆕 NEW: Collect all storage paths for tracking (enables deletion later)
  const allStoragePaths = [
    structurePath,
    ...Array.from(photoStoragePaths.values())
  ];
  
  console.log('[uploadDecoupledAlbum] ✅ Upload complete!', {
    shareId,
    photoCount: photoMap.size,
    structureSize: `${(structureBytes.byteLength / 1024).toFixed(2)} KB`,
    hashLength: hash.length
  });
  
  if (onProgress) {
    onProgress({ phase: 'complete', shareId, hash });
  }
  
  // 🆕 MODIFIED: Return storagePaths for tracking
  return { hash, shareId, photoCount: photoMap.size, storagePaths: allStoragePaths };
}

// ---------------------------------------------------------------------------
// Decoupled Download
// ---------------------------------------------------------------------------

/**
 * Load structure and photos separately
 */
export async function loadDecoupledAlbum(hash, onProgress) {
  console.log("[loadDecoupledAlbum] 🔐 Loading decoupled album...");
  
  if (!hash || !hash.startsWith(STORAGE_PREFIX)) {
    throw new Error('Invalid storage hash');
  }
  
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto not available');
  }
  
  if (!db || !storage) {
    throw new Error('Firebase not initialized');
  }
  
  // Parse hash
  const withoutPrefix = hash.slice(STORAGE_PREFIX.length);
  const parts = withoutPrefix.split('.');
  
  if (parts.length < 3) {
    throw new Error('Invalid storage hash format');
  }
  
  const [shareId, keyB64, ivB64] = parts;
  console.log(`[loadDecoupledAlbum] Share ID: ${shareId}`);
  
  // Import key
  const keyRaw = base64urlToArrayBuffer(keyB64);
  const iv = new Uint8Array(base64urlToArrayBuffer(ivB64));
  
  const key = await window.crypto.subtle.importKey(
    'raw',
    keyRaw,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );
  
  // Fetch and decrypt structure
  console.log('[loadDecoupledAlbum] 📥 Fetching structure...');
  const structurePath = `shares/${shareId}/structure.enc`;
  const fileRef = ref(storage, structurePath);
  const encryptedBytes = await getBytes(fileRef);
  
  console.log('[loadDecoupledAlbum] 🔓 Decrypting structure...');
  const plain = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encryptedBytes
  );
  
  const json = new TextDecoder().decode(plain);
  const structure = JSON.parse(json);
  
  console.log('[loadDecoupledAlbum] ✅ Structure loaded:', {
    pages: structure.pages?.length || 0,
    photos: structure.photos?.length || 0
  });
  
  if (onProgress) {
    onProgress({ phase: 'structure', loaded: true });
  }
  
  // Collect all storage paths
  const photosToLoad = new Set();
  
  if (Array.isArray(structure.photos)) {
    structure.photos.forEach(photo => {
      if (photo.storagePath) {
        photosToLoad.add(photo.storagePath);
      }
    });
  }
  
  if (Array.isArray(structure.pages)) {
    structure.pages.forEach(page => {
      if (Array.isArray(page.items)) {
        page.items.forEach(item => {
          if (item.storagePath) {
            photosToLoad.add(item.storagePath);
          }
        });
      }
      if (Array.isArray(page.photos)) {
        page.photos.forEach(photo => {
          if (photo.storagePath) {
            photosToLoad.add(photo.storagePath);
          }
        });
      }
    });
  }
  
  console.log(`[loadDecoupledAlbum] 📥 Loading ${photosToLoad.size} photos...`);
  
  // Load download URLs for all photos
  const photoUrls = new Map();
  const paths = Array.from(photosToLoad);
  
  let loaded = 0;
  const BATCH_SIZE = 10;
  
  for (let i = 0; i < paths.length; i += BATCH_SIZE) {
    const batch = paths.slice(i, i + BATCH_SIZE);
    
    const urlPromises = batch.map(async (path) => {
      try {
        const photoRef = ref(storage, path);
        const url = await getDownloadURL(photoRef);
        return { path, url };
      } catch (err) {
        console.error(`[loadDecoupledAlbum] Failed to load photo: ${path}`, err);
        return { path, url: null };
      }
    });
    
    const results = await Promise.all(urlPromises);
    
    results.forEach(({ path, url }) => {
      if (url) {
        photoUrls.set(path, url);
        loaded++;
        
        if (onProgress) {
          onProgress({
            phase: 'photos',
            loaded,
            total: photosToLoad.size,
            path
          });
        }
      }
    });
    
    console.log(`[loadDecoupledAlbum] Loaded ${loaded}/${photosToLoad.size} photos`);
  }
  
  // Reconstruct payload with download URLs
  const payload = reconstructPayloadWithUrls(structure, photoUrls);
  
  console.log('[loadDecoupledAlbum] ✅ Album fully loaded');
  
  if (onProgress) {
    onProgress({ phase: 'complete' });
  }
  
  return payload;
}

/**
 * Reconstruct payload by replacing storagePaths with download URLs
 */
function reconstructPayloadWithUrls(structure, photoUrls) {
  const payload = JSON.parse(JSON.stringify(structure)); // Deep clone
  
  // Restore photos array
  if (Array.isArray(payload.photos)) {
    payload.photos = payload.photos.map(photo => ({
      ...photo,
      url: photo.storagePath ? photoUrls.get(photo.storagePath) : null
    }));
  }
  
  // Restore photos in pages
  if (Array.isArray(payload.pages)) {
    payload.pages = payload.pages.map(page => {
      const newPage = { ...page };
      
      if (Array.isArray(newPage.items)) {
        newPage.items = newPage.items.map(item => ({
          ...item,
          url: item.storagePath ? photoUrls.get(item.storagePath) : null
        }));
      }
      
      if (Array.isArray(newPage.photos)) {
        newPage.photos = newPage.photos.map(photo => ({
          ...photo,
          url: photo.storagePath ? photoUrls.get(photo.storagePath) : null,
          src: photo.storagePath ? photoUrls.get(photo.storagePath) : null
        }));
      }
      
      return newPage;
    });
  }
  
  return payload;
}

// ---------------------------------------------------------------------------
// Public API (backwards compatible)
// ---------------------------------------------------------------------------

export function isStoredShareHash(hash) {
  return !!hash && hash.startsWith(STORAGE_PREFIX);
}

/**
 * Smart loader - detects version and uses appropriate method
 */
export async function loadStoredAlbumFromHash(hash, onProgress) {
  console.log("[loadStoredAlbumFromHash] Detecting album version...");
  
  if (!hash || !hash.startsWith(STORAGE_PREFIX)) {
    return null;
  }
  
  const withoutPrefix = hash.slice(STORAGE_PREFIX.length);
  const [shareId] = withoutPrefix.split('.');
  
  // Check if structure.enc exists (v2) or payload.enc (v1)
  try {
    const structureRef = ref(storage, `shares/${shareId}/structure.enc`);
    await getDownloadURL(structureRef);
    console.log("[loadStoredAlbumFromHash] Version 2 detected (decoupled)");
    return await loadDecoupledAlbum(hash, onProgress);
  } catch (err) {
    console.log("[loadStoredAlbumFromHash] Version 1 detected (legacy), falling back...");
    // Fall back to old method (import from original share.js if needed)
    throw new Error('Legacy format not supported in v2. Please use share.js for v1 albums.');
  }
}

/**
 * Get the correct base URL for share links
 * On Android/iOS, use production URL instead of localhost
 */
export function getShareBaseUrl() {
  const origin = window.location.origin;
  
  // If running on localhost (dev mode or Capacitor WebView), use production URL
  if (origin.includes('localhost') || origin.includes('127.0.0.1') || origin.includes('10.0.2.2')) {
    // ✅ Production URL - vivajour.com
    return 'https://vivajour.com';
  }
  
  return origin;
}

/**
 * Main share function
 * 🆕 MODIFIED: Now saves share record for tracking/management
 */
export async function shareAsStory(payload, title = 'My Album', onProgress) {
  try {
    const { hash, shareId, photoCount, storagePaths } = await uploadDecoupledAlbum(payload, 'story', onProgress);
    
    // ✅ FIX: Use production URL, not localhost
    const baseUrl = getShareBaseUrl();
    const link = `${baseUrl}/story#${hash}`;
    
    console.log('[shareAsStory] Generated link:', link);
    
    // 🆕 NEW: Save share record for tracking/management
    try {
      await saveShareRecord({
        shareId,
        albumTitle: title || payload?.trip?.name || 'Untitled Album',
        shareUrl: link,
        createdAt: Date.now(),
        photoCount: photoCount || 0,
        tripId: payload?.tripId || null,
        storagePaths: storagePaths || [],
        mode: 'story',
      });
      console.log('[shareAsStory] ✅ Share record saved for tracking');
    } catch (recordErr) {
      // Don't fail the share if record save fails
      console.warn('[shareAsStory] Failed to save share record:', recordErr);
    }
    
    // Check if running in Capacitor (Android/iOS)
    const isCapacitor = typeof window !== 'undefined' && 
                        window.Capacitor && 
                        window.Capacitor.isNativePlatform();
    
    if (isCapacitor) {
      // Try Capacitor Share plugin (if installed)
      try {
        // Use dynamic import with try-catch for when plugin isn't installed
        const ShareModule = await import('@capacitor/share').catch(() => null);
        if (ShareModule && ShareModule.Share) {
          await ShareModule.Share.share({
            title: title,
            text: `Check out my album: ${title}`,
            url: link,
            dialogTitle: 'Share Album'
          });
          console.log('[shareAsStory] ✅ Shared via Capacitor');
          return { success: true, link, shareId };
        }
      } catch (err) {
        console.warn('[shareAsStory] Capacitor Share not available:', err);
      }
      
      // Fallback: Try navigator.share (works in some WebViews)
      if (navigator.share) {
        try {
          await navigator.share({ title, url: link });
          console.log('[shareAsStory] ✅ Shared via navigator.share');
          return { success: true, link, shareId };
        } catch (err) {
          if (err && err.name === 'AbortError') {
            return { success: false, cancelled: true };
          }
          console.warn('[shareAsStory] navigator.share failed:', err);
        }
      }
    } else if (navigator.share) {
      // Use Web Share API for browsers that support it
      try {
        await navigator.share({ title, url: link });
        console.log('[shareAsStory] ✅ Shared via Web Share API');
        return { success: true, link, shareId };
      } catch (err) {
        if (err && err.name === 'AbortError') {
          return { success: false, cancelled: true };
        }
        console.warn('[shareAsStory] Web Share failed:', err);
        // Fall through to clipboard
      }
    }
    
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(link);
      console.log('[shareAsStory] ✅ Link copied to clipboard');
      return { success: true, link, copied: true, shareId };
    } catch (err) {
      console.warn('[shareAsStory] Clipboard failed:', err);
      // Last resort: return link so UI can show it
      return { success: true, link, manualCopy: true, shareId };
    }
  } catch (err) {
    console.error('[shareAsStory] Failed:', err);
    return { success: false, error: err };
  }
}

/**
 * Preview function
 * 🆕 MODIFIED: Now saves share record for tracking/management
 */
export async function previewStoryLocally(payload, onProgress) {
  try {
    const { hash, shareId, photoCount, storagePaths } = await uploadDecoupledAlbum(payload, 'story-preview', onProgress);
    
    // ✅ FIX: Use production URL for preview links too
    const baseUrl = getShareBaseUrl();
    const link = `${baseUrl}/story#${hash}`;
    
    console.log('[previewStoryLocally] Opening:', link);
    
    // 🆕 NEW: Save share record for tracking/management
    try {
      await saveShareRecord({
        shareId,
        albumTitle: payload?.trip?.name || 'Preview',
        shareUrl: link,
        createdAt: Date.now(),
        photoCount: photoCount || 0,
        tripId: payload?.tripId || null,
        storagePaths: storagePaths || [],
        mode: 'preview',
      });
      console.log('[previewStoryLocally] ✅ Share record saved for tracking');
    } catch (recordErr) {
      // Don't fail the preview if record save fails
      console.warn('[previewStoryLocally] Failed to save share record:', recordErr);
    }
    
    window.open(link, '_blank', 'noopener,noreferrer');
    return link;
  } catch (err) {
    console.error('[previewStoryLocally] Failed:', err);
    throw err;
  }
}

// Re-export other utilities for compatibility
export { randomId, isCryptoAvailable };